import type { Collaborator, Role } from '@cmoh/shared'
import { hasAtLeast, ROLE } from '@cmoh/shared'
import { useMemo } from 'react'
import { useAuthContext } from '@/features/auth/hooks/use-auth-context'

export interface PermissionConfig {
  minRole?: Role
  roles?: Role[]
  superAdminOnly?: boolean
  superAdminBypass?: boolean
  requireLeadership?: boolean
  allowSelf?: boolean
  allowHighRolesBypass?: boolean
}

export interface PermissionResult {
  hasPermission: boolean
  isSuperAdmin: boolean
  hasLeadership: boolean
  isSelf: boolean
  userRole?: Role
  targetRole?: Role
  denialReason?: string
}

/**
 * Hook for evaluating permissions based on roles and leadership hierarchy.
 *
 * This hook provides a comprehensive way to check if a user has permission
 * to perform actions on a collaborator based on:
 * - Role hierarchy (minRole, roles)
 * - Super admin status
 * - Leadership relationships
 * - Self-access permissions
 * - High role bypass (Owner/Admin can access anyone)
 *
 * @param targetCollaborator - The collaborator being accessed
 * @param config - Permission configuration
 * @returns Permission evaluation result
 */
export function usePermissions(
  targetCollaborator?: Collaborator,
  config: PermissionConfig = {},
): PermissionResult {
  const { user, membership, isLoading } = useAuthContext()

  return useMemo(() => {
    const result: PermissionResult = {
      hasPermission: false,
      isSuperAdmin: false,
      hasLeadership: false,
      isSelf: false,
      userRole: undefined,
      targetRole: undefined,
      denialReason: undefined,
    }

    if (isLoading) {
      result.denialReason = 'LOADING'
      return result
    }

    if (!user || (!membership && !user.isSuperAdmin)) {
      result.denialReason = 'NOT_AUTHENTICATED'
      return result
    }

    const userRole = membership?.role ?? undefined
    const targetRole = targetCollaborator?.role
    const isSuperAdmin = Boolean(user.isSuperAdmin)
    const isSelf = targetCollaborator ? user.id === targetCollaborator.userId : false

    result.userRole = userRole
    result.targetRole = targetRole
    result.isSuperAdmin = isSuperAdmin
    result.isSelf = isSelf

    let hasLeadership = false

    if (targetCollaborator?.leaders) {
      hasLeadership = targetCollaborator.leaders.some((leader) => leader.userId === user.id)
    }

    result.hasLeadership = hasLeadership

    if (isSuperAdmin && config.superAdminBypass !== false) {
      result.hasPermission = true
      return result
    }

    if (config.superAdminOnly) {
      result.hasPermission = isSuperAdmin
      result.denialReason = isSuperAdmin ? undefined : 'Requires super admin'
      return result
    }

    if (targetCollaborator && config.allowSelf && isSelf) {
      result.hasPermission = true
      return result
    }

    if (!userRole) {
      result.denialReason = 'NO_ROLE_ASSIGNED'
      return result
    }

    if (config.roles && config.roles.length > 0) {
      const hasRole = config.roles.includes(userRole)
      if (!hasRole) {
        result.denialReason = 'REQUIRES_SPECIFIC_ROLES'
        return result
      }
    }

    if (config.minRole) {
      const hasMinRole = hasAtLeast(userRole, config.minRole)
      if (!hasMinRole) {
        result.denialReason = 'REQUIRES_MINIMUM_ROLE'
        return result
      }
    }

    if (config.allowHighRolesBypass !== false) {
      if (userRole === ROLE.OWNER || userRole === ROLE.ADMIN) {
        if (targetCollaborator && userRole === ROLE.ADMIN && targetRole === ROLE.OWNER) {
          result.denialReason = 'ADMIN_CANNOT_ACCESS_OWNER'
          return result
        }
        result.hasPermission = true
        return result
      }
    }

    if (targetCollaborator && config.requireLeadership && !result.hasLeadership) {
      result.denialReason = 'REQUIRES_LEADERSHIP'
      return result
    }

    result.hasPermission = true
    return result
  }, [user, membership, targetCollaborator, config, isLoading])
}

/**
 * Hook for checking if user can edit a collaborator's profile.
 *
 * Rules:
 * - Owner/Admin can edit anyone (except Admin cannot edit Owner)
 * - Leaders can edit their subordinates
 * - Users can edit themselves
 *
 * @param targetCollaborator - The collaborator being edited
 * @returns Permission result for editing
 */
export function useCanEditCollaborator(targetCollaborator?: Collaborator) {
  return usePermissions(targetCollaborator, {
    minRole: ROLE.MANAGER,
    allowSelf: true,
    requireLeadership: true,
    allowHighRolesBypass: true,
  })
}

/**
 * Hook for checking if user can change a collaborator's role.
 *
 * Rules:
 * - Owner can change anyone's role
 * - Admin can change anyone's role except Owner
 * - Leaders can change subordinates roles
 * - Users cannot change their own role
 * - Cannot assign roles higher than own role
 *
 * @param targetCollaborator - The collaborator whose role is being changed
 * @returns Permission result for role changes
 */
export function useCanChangeRole(targetCollaborator?: Collaborator) {
  return usePermissions(targetCollaborator, {
    minRole: ROLE.ADMIN,
    allowSelf: false,
    requireLeadership: true,
    allowHighRolesBypass: true,
  })
}

/**
 * Hook for checking if user can view sensitive collaborator data.
 *
 * Rules:
 * - Owner/Admin can view anyone's sensitive data
 * - Leaders can view subordinates sensitive data
 * - Users can view their own sensitive data
 *
 * @param targetCollaborator - The collaborator being viewed
 * @returns Permission result for viewing sensitive data
 */
export function useCanViewSensitiveData(targetCollaborator?: Collaborator) {
  return usePermissions(targetCollaborator, {
    minRole: ROLE.MANAGER,
    allowSelf: true,
    requireLeadership: true,
    allowHighRolesBypass: true,
  })
}

/**
 * Hook for checking if user can manage collaborator tags.
 *
 * Rules:
 * - Owner/Admin can manage anyone's tags
 * - Leaders can manage subordinates tags
 * - Users can manage their own tags
 *
 * @param targetCollaborator - The collaborator whose tags are being managed
 * @returns Permission result for tag management
 */
export function useCanManageTags(targetCollaborator?: Collaborator) {
  return usePermissions(targetCollaborator, {
    minRole: ROLE.MANAGER,
    allowSelf: true,
    requireLeadership: true,
    allowHighRolesBypass: true,
  })
}

// === GENERAL ROLE-BASED PERMISSIONS ===

/**
 * Hook for checking if user has a specific role or higher, or is a super admin.
 *
 * @param requiredRole - The minimum role required
 * @returns Whether the user has the required role
 */
export function useHasRole(requiredRole: Role) {
  const { user, membership, isLoading } = useAuthContext()

  return useMemo(() => {
    if (isLoading || !user) return false
    if (user.isSuperAdmin) return true
    const role = membership?.role
    if (!role) return false
    return hasAtLeast(role, requiredRole)
  }, [user, membership, requiredRole, isLoading])
}

/**
 * Hook for checking if user is a super admin.
 *
 * @returns Whether the user is a super admin
 */
export function useIsSuperAdmin() {
  const { user, isLoading } = useAuthContext()

  return useMemo(() => {
    if (isLoading || !user) return false
    return Boolean(user.isSuperAdmin)
  }, [user, isLoading])
}

/**
 * Hook for checking if user is an admin or higher.
 *
 * @returns Whether the user is admin or higher
 */
export function useIsAdmin() {
  return useHasRole(ROLE.ADMIN)
}

/**
 * Hook for checking if user is an owner or a super admin.
 *
 * @returns Whether the user is an owner or a super admin
 */
export function useIsOwner() {
  const { user, membership, isLoading } = useAuthContext()

  return useMemo(() => {
    if (isLoading || !user) return false
    if (user.isSuperAdmin) return true
    return membership?.role === ROLE.OWNER
  }, [user, membership, isLoading])
}

/**
 * Hook for checking if user is a manager or higher.
 *
 * @returns Whether the user is manager or higher
 */
export function useIsManager() {
  return useHasRole(ROLE.MANAGER)
}
